import React, { useState, useEffect, useCallback } from 'react';
import { deductionAPI, staffAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';

export default function DeductionsTab() {
  const { user } = useAuth();
  const { formatMoney } = useCurrency();
  const [types, setTypes]           = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [staff, setStaff]           = useState([]);
  const [addingType, setAddingType] = useState(false);
  const [addingAssign, setAddingAssign] = useState(false);
  const [typeForm, setTypeForm] = useState({
    name: '', type: 'percentage', value: '', applies_to: 'gross', recurring: true, active: true
  });
  const [assignForm, setAssignForm] = useState({ employee_id: '', deduction_type_id: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, a] = await Promise.all([
        deductionAPI.getDeductionTypes(),
        deductionAPI.getEmployeeDeductions(),
      ]);
      setTypes(t.data?.deduction_types || []);
      setAssignments(a.data?.assignments || []);
    } catch {}
  }, []);

  // Load all tenant staff for the assignment dropdown using the same
  // endpoint as Staff.js — works for owners, managers, and super_admins.
  const loadStaff = useCallback(async () => {
    try {
      const r = await staffAPI.getWithLocations();
      setStaff(r.data || []);
    } catch {
      // Fail silently — dropdown will show "No staff loaded" fallback
    }
  }, []);

  useEffect(() => { load(); loadStaff(); }, [load, loadStaff]);

  const handleAddType = async () => {
    if (!typeForm.name || !typeForm.value) return;
    setSaving(true);
    try {
      await deductionAPI.createDeductionType({...typeForm, value: parseFloat(typeForm.value)});
      toast.success('Deduction type added');
      setTypeForm({ name: '', type: 'percentage', value: '', applies_to: 'gross', recurring: true, active: true });
      setAddingType(false);
      load();
    } catch { toast.error('Failed to add deduction type'); }
    finally { setSaving(false); }
  };

  const handleDeleteType = async (id) => {
    try { await deductionAPI.deleteDeductionType(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleAssign = async () => {
    if (!assignForm.employee_id || !assignForm.deduction_type_id) return;
    setSaving(true);
    try {
      await deductionAPI.assignDeduction(assignForm);
      toast.success('Deduction assigned');
      setAssignForm({ employee_id: '', deduction_type_id: '' });
      setAddingAssign(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleRemoveAssign = async (id) => {
    try { await deductionAPI.removeAssignment(id); load(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="deductions-tab">

      {/* Deduction Types */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex justify-between items-center mb-3">
          <p className="text-sm font-semibold text-slate-800">Deduction Types</p>
          {['owner', 'super_admin'].includes(user?.role) && (
            <button onClick={() => setAddingType(a => !a)} data-testid="add-deduction-type-btn"
              className="flex items-center gap-1 text-xs font-medium text-orange-500 hover:text-orange-600">
              <Plus size={13} /> Add Deduction
            </button>
          )}
        </div>

        {addingType && (
          <div className="border border-slate-100 rounded-lg p-3 mb-3 bg-slate-50 space-y-2">
            <input placeholder="Name (e.g. Tax, Health Insurance)"
              value={typeForm.name}
              onChange={e => setTypeForm(f => ({...f, name: e.target.value}))}
              className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs"
              data-testid="deduction-name" />
            <div className="grid grid-cols-2 gap-2">
              <select value={typeForm.type}
                onChange={e => setTypeForm(f => ({...f, type: e.target.value}))}
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs"
                data-testid="deduction-calc-type">
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed amount</option>
              </select>
              <input type="number" placeholder="Value"
                value={typeForm.value}
                onChange={e => setTypeForm(f => ({...f, value: e.target.value}))}
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs"
                data-testid="deduction-value" />
              <select value={typeForm.applies_to}
                onChange={e => setTypeForm(f => ({...f, applies_to: e.target.value}))}
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs"
                data-testid="deduction-applies-to">
                <option value="gross">% of gross</option>
                <option value="base">% of base</option>
              </select>
              <label className="flex items-center gap-1 text-xs text-slate-600">
                <input type="checkbox" checked={typeForm.recurring}
                  onChange={e => setTypeForm(f => ({...f, recurring: e.target.checked}))} />
                Recurring
              </label>
            </div>
            <button onClick={handleAddType} disabled={saving}
              className="w-full px-3 py-1.5 text-xs bg-slate-800 text-white rounded-lg disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Deduction Type'}
            </button>
          </div>
        )}

        {types.length === 0 ? (
          <p className="text-xs text-slate-400">No deduction types. Add tax, health insurance, etc.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {types.map(t => (
              <div key={t.id} className="flex items-center justify-between py-2" data-testid={`deduction-type-row-${t.id}`}>
                <div>
                  <p className="text-sm text-slate-700">{t.name}</p>
                  <p className="text-xs text-slate-400">
                    {t.type === 'percentage' ? `${t.value}% of ${t.applies_to}` : `${formatMoney(t.value)} fixed`}
                    {t.recurring && ' · recurring'}
                  </p>
                </div>
                {['owner', 'super_admin'].includes(user?.role) && (
                  <button onClick={() => handleDeleteType(t.id)} className="p-1.5 text-slate-400 hover:text-red-500">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Employee Assignments */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex justify-between items-center mb-3">
          <p className="text-sm font-semibold text-slate-800">Employee Assignments</p>
          {types.length > 0 && (
            <button onClick={() => setAddingAssign(a => !a)} data-testid="assign-deduction-btn"
              className="flex items-center gap-1 text-xs font-medium text-orange-500 hover:text-orange-600">
              <Plus size={13} /> Assign
            </button>
          )}
        </div>

        {addingAssign && (
          <div className="border border-slate-100 rounded-lg p-3 mb-3 bg-slate-50 space-y-2">
            <select value={assignForm.employee_id}
              onChange={e => setAssignForm(f => ({...f, employee_id: e.target.value}))}
              className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs"
              data-testid="assign-employee-id">
              <option value="">Select employee</option>
              {staff.length > 0
                ? staff.map(s => <option key={s.id} value={s.id}>{s.name || s.email}</option>)
                : <option disabled>No staff loaded — enter ID manually</option>
              }
            </select>
            <select value={assignForm.deduction_type_id}
              onChange={e => setAssignForm(f => ({...f, deduction_type_id: e.target.value}))}
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs"
              data-testid="assign-deduction-type">
              <option value="">Select deduction</option>
              {types.filter(t => t.active).map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <button onClick={handleAssign} disabled={saving}
              className="w-full px-3 py-1.5 text-xs bg-slate-800 text-white rounded-lg disabled:opacity-50"
              data-testid="assign-submit-btn">
              {saving ? 'Assigning…' : 'Assign'}
            </button>
          </div>
        )}

        {assignments.length === 0 ? (
          <p className="text-xs text-slate-400">No assignments. Assign deductions to employees.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {assignments.map(a => (
              <div key={a.id} className="flex items-center justify-between py-2" data-testid={`assignment-row-${a.id}`}>
                <div>
                  <p className="text-sm text-slate-700">{a.employee_name || a.employee_id}</p>
                  <p className="text-xs text-slate-400">{a.deduction_name}</p>
                </div>
                <button onClick={() => handleRemoveAssign(a.id)} className="p-1.5 text-slate-400 hover:text-red-500">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
